import { Column, Dropdown, FormGroup, FormLabel, RadioButton, RadioButtonGroup, Row, TextInput } from "carbon-components-react"; // prettier-ignore
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDonationAmount, useProcessingFee } from "./utils";
import { fetchBeneficiaries } from "../../actions/beneficiaries";
import { submitDonation } from "../../actions/donations";
import { fetchTeams } from "../../actions/teams";
import { getBeneficiariesData, getTeamsData } from "../../selectors"; // prettier-ignore
import { useDonations } from "../../utils/donations";
import { addCommas } from "../../utils/formatting";
import { useValidation, validateDonationAmount, validateNotEmpty } from "../../utils/validation"; // prettier-ignore
import CYCPaymentModal from "./CYCPaymentModal";
import PayPalPaymentModal from "./PayPalPaymentModal";

const defaultState = {
  payment_selection_method: "Credit Card or PayPal",
  processing_fee_selection: "radio-yes",
  selected_team: "",
  selected_beneficiary: "",
  original_amount: "",
  input_address: "",
  input_city: "",
  input_state: "",
  input_zip: "",
};

const validators = {
  payment_selection_method: [],
  processing_fee_selection: [],
  selected_team: [validateNotEmpty],
  selected_beneficiary: [validateNotEmpty],
  original_amount: [validateNotEmpty, validateDonationAmount],
  input_address: [validateNotEmpty],
  input_city: [validateNotEmpty],
  input_state: [validateNotEmpty],
  input_zip: [validateNotEmpty],
};

const DonationForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const beneficiariesData = useSelector(getBeneficiariesData);
  const beneficiariesArray = useMemo(() => beneficiariesData.map((item) => item["beneficiary_name"]), [beneficiariesData]); // prettier-ignore

  const teamDonations = useDonations();
  const teamsData = useSelector(getTeamsData);
  const teamsArray = useMemo(() => teamsData.map((item) => item["team_name"]), [teamsData]); // prettier-ignore

  const [form, setForm] = useState(defaultState);
  const [validation, invalid] = useValidation(form, validators);
  const [donation, setDonation] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const finalProcessingFee = useProcessingFee(form.payment_selection_method);
  const totalDonationAmount = useDonationAmount(form.original_amount, form.processing_fee_selection, finalProcessingFee); // prettier-ignore
  const processingFeeAmount = useMemo(() => (parseFloat(totalDonationAmount) - parseFloat(form.original_amount)).toFixed(2), [totalDonationAmount, form.original_amount]); // prettier-ignore

  const createDonation = (details) => {
    const donation = {
      id: new Date().getTime().toString(36),
      donation_amount: totalDonationAmount,
      processing_fee_amount: processingFeeAmount,
      donation_data: new Date().toISOString(),
      account_number: "N/A",
      comment: "N/A",
      ...form,
      ...details,
    };
    setDonation(donation);
    return true;
  };

  useEffect(() => {
    dispatch(fetchBeneficiaries());
    dispatch(fetchTeams());
  }, [dispatch]);

  useEffect(() => {
    if (donation && !submitted) {
      setSubmitted(true);
      dispatch(submitDonation(donation, teamDonations, teamsData));
    }
  }, [dispatch, navigate, donation, submitted, teamDonations, teamsData]);

  useEffect(() => {
    if (submitted) {
      navigate("/");
    }
  }, [navigate, submitted]);

  return (
    <div className="donation-page form-page">
      <h4>Donate To A Team</h4>
      <div style={{ textAlign: "left" }}>
        <FormGroup invalid={invalid} legendText="" message={false}>
          <Dropdown
            id="donationform-team"
            invalid={
              Boolean(validation.selected_team) && form.original_amount !== ""
            }
            invalidText={validation.selected_team}
            titleText="I am donating via Team:"
            label="Select Team"
            items={teamsArray}
            selectedItem={form.selected_team}
            onChange={(e) =>
              setForm({ ...form, selected_team: e.selectedItem })
            }
          />
          <Dropdown
            id="donationform-beneficiary"
            invalid={
              Boolean(validation.selected_beneficiary) &&
              form.original_amount !== ""
            }
            invalidText={validation.selected_beneficiary}
            titleText="My donation is to benefit:"
            label="Select Beneficiary"
            items={beneficiariesArray}
            selectedItem={form.selected_beneficiary}
            onChange={(e) =>
              setForm({ ...form, selected_beneficiary: e.selectedItem })
            }
          />
          <TextInput
            id="donationform-address"
            invalid={
              Boolean(validation.input_address) && form.original_amount !== ""
            }
            invalidText={validation.input_address}
            labelText="My home address is:"
            light={true}
            type="text"
            value={form.input_address}
            onChange={(e) =>
              setForm({ ...form, input_address: e.target.value })
            }
          />
          <Row>
            <Column>
              <TextInput
                style={{ width: "200px" }}
                id="donationform-city"
                invalid={
                  Boolean(validation.input_city) && form.original_amount !== ""
                }
                invalidText={validation.input_city}
                labelText="City:"
                light={true}
                type="text"
                value={form.input_city}
                onChange={(e) =>
                  setForm({ ...form, input_city: e.target.value })
                }
              />
            </Column>
            <Column className="state-input">
              <TextInput
                style={{ width: "120px" }}
                id="donationform-state"
                invalid={
                  Boolean(validation.input_state) && form.original_amount !== ""
                }
                invalidText={validation.input_state}
                labelText="State:"
                light={true}
                type="text"
                value={form.input_state}
                onChange={(e) =>
                  setForm({ ...form, input_state: e.target.value })
                }
              />
            </Column>
            <Column>
              <TextInput
                style={{ width: "115px" }}
                id="donationform-zip"
                invalid={
                  Boolean(validation.input_zip) && form.original_amount !== ""
                }
                invalidText={validation.input_zip}
                labelText="Zip:"
                light={true}
                type="text"
                value={form.input_zip}
                onChange={(e) =>
                  setForm({ ...form, input_zip: e.target.value })
                }
              />
            </Column>
          </Row>
          <TextInput
            id="donationform-donationamount"
            invalid={
              Boolean(validation.original_amount) && form.original_amount !== ""
            }
            invalidText={validation.original_amount}
            labelText="My donation amount is:"
            light={true}
            type="text"
            value={form.original_amount}
            onChange={(e) =>
              setForm({ ...form, original_amount: e.target.value })
            }
          />
          <Row>
            <Column>
              <Dropdown
                id="donationform-team"
                titleText="I will pay with:"
                label="Select Method"
                initialSelectedItem="Credit Card or PayPal"
                items={["Credit Card or PayPal", "CYC Account"]}
                selectedItem={form.payment_selection_method}
                onChange={(e) =>
                  setForm({
                    ...form,
                    payment_selection_method: e.selectedItem,
                  })
                }
              />
            </Column>
          </Row>
          <Row>
            <Column>
              <RadioButtonGroup
                legendText={
                  "I will cover " +
                  finalProcessingFee +
                  "% processing fee for charity."
                }
                name="donationform-processingfee"
                defaultSelected="radio-yes"
                onChange={(e) =>
                  setForm({ ...form, processing_fee_selection: e })
                }
                valueSelected={form.processing_fee_selection}
              >
                <RadioButton
                  labelText="Yes"
                  value="radio-yes"
                  id="donationform-radio-1"
                />
                <RadioButton
                  labelText="No"
                  value="radio-no"
                  id="donationform-radio-2"
                />
              </RadioButtonGroup>
            </Column>
            <Column>
              <FormLabel>Total Donation Amount:</FormLabel>
              <p>${addCommas(totalDonationAmount)}</p>
            </Column>
          </Row>
          <br />
        </FormGroup>
        {!submitted && (
          <>
            {form.payment_selection_method === "Credit Card or PayPal" ? (
              <PayPalPaymentModal
                disabled={invalid}
                onSubmit={createDonation}
                totalDonationAmount={totalDonationAmount}
              />
            ) : (
              <CYCPaymentModal
                disabled={invalid}
                onSubmit={createDonation}
                totalDonationAmount={totalDonationAmount}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DonationForm;
